import { useMemo } from 'react';
import type { DrawerProps } from '@mui/material';
import { VyDrawer, VyThemeProvider } from '@vy/ui-design-system';
import {
  isCommercialTermsProduct,
  isCommissionProduct,
  isOneTimeProduct,
  isRefundProduct,
  isSubscriptionProduct,
} from '@vy/services';
import type { FixedProductInput } from '@vy/types';
import { DEFAULT_CURRENCY } from '@vy/types';
import { raise, vySerialize } from '@vy/utils';
import { ChargeProvider } from '../charge-context';
import { useModifyPanel } from '../modify-panel-shared/hooks/use-modify-panel';
import {
  CommercialTermsVariant,
  CommissionVariant,
  OneTimeVariant,
  RefundVariant,
  SubscriptionVariant,
} from './variants';

export type FixedFeesModifyPanelProps = Pick<DrawerProps, 'open' | 'onClose'> & {
  fixedProduct: FixedProductInput;
  isInvoice?: boolean;
  isParentAgreement?: boolean;
};

export function FixedFeesModifyPanel({
  fixedProduct,
  isInvoice,
  isParentAgreement,
  ...props
}: FixedFeesModifyPanelProps) {
  const Variant = useMemo(() => pickVariant(fixedProduct), [fixedProduct]);

  // A refund's only required field (price) lives in the on-page table, not in
  // this drawer, so it bypasses the save-time validation gate.
  const { productIndex, drawerTitle, handleClose, handleCancel, handleSave } = useModifyPanel({
    arrayName: 'fixedProducts',
    product: fixedProduct,
    open: props.open,
    onClose: props.onClose,
    shouldBypassValidationGate: isRefundProduct,
  });

  return (
    <VyThemeProvider>
      <VyDrawer
        open={Boolean(props.open)}
        onClose={handleClose}
        width="50%"
        title={drawerTitle}
        subtitle="Configure pricing, schedule, and payment settings for this item."
        onCancel={handleCancel}
        onSave={handleSave}
        data-vy="modify-fixed-product-drawer"
      >
        <ChargeProvider
          value={{
            item: fixedProduct,
            itemIndex: productIndex,
            editable: true,
            currency: DEFAULT_CURRENCY,
            isInvoice,
            isParentAgreement,
          }}
        >
          <Variant />
        </ChargeProvider>
      </VyDrawer>
    </VyThemeProvider>
  );
}

function pickVariant(product: FixedProductInput): React.FC {
  if (isOneTimeProduct(product)) return OneTimeVariant;
  if (isSubscriptionProduct(product)) return SubscriptionVariant;
  if (isCommercialTermsProduct(product)) return CommercialTermsVariant;
  if (isCommissionProduct(product)) return CommissionVariant;
  if (isRefundProduct(product)) return RefundVariant;
  return raise(`unknown product type ${vySerialize(product, null, 2)}`);
}
